import { ChatInitData } from '@agent/agent-interface/contact-view/chat/model/chat-init-data';
import { ChatMessage } from '@agent/agent-interface/contact-view/chat/model/chat-message';
import { ChatModel } from '@agent/agent-interface/contact-view/chat/model/chat-model';
import { ChatSlaConf } from '@agent/agent-interface/contact-view/chat/model/chat-sla-conf';
import { ChatSystemMessage } from '@agent/agent-interface/contact-view/chat/model/chat-system-message';
import { DataBuilder } from '@agent/core/contact/util/data-builder';
import { Injectable } from '@angular/core';
import { filter, interval, map, Observable, Subject, take, takeUntil } from 'rxjs';

import { AgentConfig, AgentConfigFacade, AppConf, Booking, BookingFacade, ChannelType, HttpService, Logger } from '@contact/upgrade';
import { MessageType } from '@agent/agent-interface/contact-view/chat/model/message-type';
import { IChatDiscussion } from '@agent/core/channel-digit/chat/models/chat-discussion';

/**
 * Class that gathers different parts of a chat conversation until all mandatory parts are retrieved.
 * Then emits the new chat object in the dedicated Observable.
 *
 * @author Brice Lepeigneul
 */
@Injectable({
  providedIn: 'root',
})
export class ChatContactBuilderService {
  private static readonly PARTIAL_DATA_COUNT = 2;
  private static readonly CHAT_PREFIX = 'CHAT_';
  private static readonly STATE_DURATION_FOR_ANIMATION_MS = 1000;

  private readonly waitingContactByConnectionId = new Map<string, Booking>();
  private readonly waitingInitData = new Map<string, ChatInitData>();
  private readonly dataBuilder: DataBuilder;
  private _agentConfig: AgentConfig;
  private chatBaseUrl: string;

  private readonly _newChatContact$ = new Subject<{ chat: ChatModel; systemMessages: ChatSystemMessage[] }>();
  readonly newChatContact$ = this._newChatContact$.asObservable();

  private readonly _destroy$ = new Subject<void>();
  readonly destroy$ = this._destroy$.asObservable();

  constructor(
    private readonly logger: Logger,
    private readonly appConf: AppConf,
    private readonly http: HttpService,
    private readonly contactFacade: BookingFacade,
    private readonly agentConfigFacade: AgentConfigFacade
  ) {
    this.dataBuilder = new DataBuilder(this.logger, id => this.onDataCompleted(id), ChatContactBuilderService.PARTIAL_DATA_COUNT);
    this.appConf.pipe(takeUntil(this.destroy$)).subscribe(({ chat }) => (this.chatBaseUrl = (chat?.chatBaseUrl || '') + '/api'));
  }

  init() {
    this.agentConfigFacade.config$.pipe(takeUntil(this.destroy$)).subscribe(config => (this._agentConfig = config));

    this.contactFacade.created$
      .pipe(
        filter(({ booking }) => booking.channel.type === ChannelType.CHAT && !this.waitingContactByConnectionId.has(booking.connectionId)),
        takeUntil(this.destroy$)
      )
      .subscribe(({ booking }) => {
        this.logger.debug(`[chat] New chat contact registered ${booking.responseId}`);
        this.waitingContactByConnectionId.set(booking.connectionId, booking);
        this.dataBuilder.savePartialData(ChatContactBuilderService.CHAT_PREFIX + booking.connectionId);
        setTimeout(() => {
          if (!this.waitingInitData.get(booking.connectionId) && this.waitingContactByConnectionId.get(booking.connectionId)) {
            this.retrieveMessages(booking.connectionId).subscribe(messages => {
              // only if we retrieve the conversation, otherwise that means the conversation is already closed
              if (messages && messages.length) {
                if (!messages.some(message => message.type === MessageType.JOIN)) {
                  const join = messages[0];
                  join.type = MessageType.JOIN;
                  join.content = '';
                  messages.push(join);
                }
                this.addChatMessages(messages);
                this.dataBuilder.savePartialData(ChatContactBuilderService.CHAT_PREFIX + booking.connectionId);
              } else if (booking.customerId) {
                this.retrieveDiscussion(booking.customerId).subscribe(discussion => {
                  if (discussion.messages && discussion.messages.length) {
                    const toChatMessage = discussion.messages.map(
                      m =>
                        new ChatMessage(
                          m.type,
                          m.content,
                          '',
                          '',
                          '',
                          m.sender !== '' ? m.senderId : '',
                          m.sender === '' ? m.senderId : '',
                          '',
                          '',
                          m.date.getTime()
                        )
                    );
                    if (!toChatMessage.some(message => message.type === MessageType.JOIN)) {
                      const join = toChatMessage[0];
                      join.type = MessageType.JOIN;
                      join.content = '';
                      toChatMessage.push(join);
                    }
                    this.addChatMessages(toChatMessage);
                    this.dataBuilder.savePartialData(ChatContactBuilderService.CHAT_PREFIX + booking.connectionId);
                  }
                });
              } else {
                window.location.reload();
              }
            });
          }
        }, 65000);
      });

    this.newChatContact$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => this.logger.info(`[chat] new chat created ${data.chat.customerId}`));
  }

  destroy() {
    this._destroy$.next();
  }

  /**
   * Handles initial bulk of chat [system] messages. Groups the messages by contactId and add part to the data builder
   * @param {ChatInitData} data bulk of chat [system] messages
   */
  setInitData(data: ChatInitData): void {
    const chatMessages = data.messages;
    const contactIds = this.addChatMessages(chatMessages);
    this.addChatSystemMessages(data.systemMessages);
    contactIds.forEach(contactId => {
      this.dataBuilder.savePartialData(ChatContactBuilderService.CHAT_PREFIX + contactId);
    });
  }

  /**
   * Clean the maps for this contact entry
   * @param connectionId
   */
  closeContact(connectionId: string) {
    this.dataBuilder.clear(ChatContactBuilderService.CHAT_PREFIX + connectionId);
    this.waitingContactByConnectionId.delete(connectionId);
    this.waitingInitData.delete(connectionId);
  }

  /**
   * Add single chat message to the local store of message until the chat is fully built.
   * Add part to the data builder if not already done.
   * @param {ChatMessage} message new element to add to the local message store
   */
  addChatMessage(message: ChatMessage): void {
    const contactIds = this.addChatMessages([message]);
    contactIds.forEach(contactId => {
      this.dataBuilder.savePartialData(ChatContactBuilderService.CHAT_PREFIX + contactId);
    });
  }

  /**
   * Add single chat system message to the local store of message until the chat is fully built.
   * @param {ChatSystemMessage} systemMessage new element to add to the local message store
   */
  addChatSystemMessage(systemMessage: ChatSystemMessage): void {
    this.addChatSystemMessages([systemMessage]);
  }

  /**
   * Add all messages into pending store, until chat is fully built
   * @param chatMessages chat messages to add to the pending list
   * @return list of new contactId. If a contactId was already known, it's not included in the list.
   */
  private addChatMessages(chatMessages: ChatMessage[]): string[] {
    return chatMessages.reduce((prev: string[], curr) => {
      if (curr.contactId != null) {
        // const group: ChatInitData = prev.get(curr.contactId) || {messages: [], systemMessages: []};
        let group = this.waitingInitData.get(curr.contactId);
        if (!group) {
          prev.push(curr.contactId);
          group = { messages: [], systemMessages: [] };
        }
        group.messages.push(curr);
        this.waitingInitData.set(curr.contactId, group);
      }
      return prev;
    }, []);
  }

  private addChatSystemMessages(systemMessages: ChatSystemMessage[]): void {
    systemMessages.forEach(systemMessage => {
      if (systemMessage.contactId) {
        const group: ChatInitData = this.waitingInitData.get(systemMessage.contactId) || { messages: [], systemMessages: [] };
        group.systemMessages.push(systemMessage);
        this.waitingInitData.set(systemMessage.contactId, group);
      }
    }, this.waitingInitData);
  }

  private onDataCompleted(id: string): void {
    const contactId = id.substr(ChatContactBuilderService.CHAT_PREFIX.length);
    const initData = this.waitingInitData.get(contactId);
    const contact = this.waitingContactByConnectionId.get(contactId);
    if (initData && contact) {
      const customerId = initData.messages[0].customerId;
      if (customerId) {
        this.fetchMessagesForCustomerId(customerId).subscribe(messages => {
          initData.messages = messages;
          this.newChat(customerId, initData, contact);
        });
      }
    }
  }

  private newChat(customerId: string, initData: ChatInitData, contact: Booking): void {
    const slaConf = this.getSlaConf(contact);
    const chatModel = new ChatModel(customerId, contact, initData.messages, slaConf);

    interval(ChatContactBuilderService.STATE_DURATION_FOR_ANIMATION_MS)
      .pipe(take(1))
      .subscribe(() => {
        chatModel.isNew = false;
      });

    this._newChatContact$.next({ chat: chatModel, systemMessages: initData.systemMessages });
  }

  private getSlaConf(contact: Booking): ChatSlaConf | undefined {
    if (this._agentConfig) {
      const skills = contact.skills.length > 0 ? contact.skills : (contact.forward && contact.forward.previousSkills) || [];
      let slaConfs = this._agentConfig.chat.slaBySkills
        .filter(conf => skills.indexOf(conf.skillName) > -1)
        .map(result => result.slaConf)
        .sort(ChatSlaConf.compare);
      if (slaConfs.length > 0) {
        return slaConfs[0];
      }
    }
    return undefined;
  }

  private retrieveMessages(contactId: string): Observable<ChatMessage[]> {
    return this.http.get<ChatMessage[]>(`${this.chatBaseUrl + '/hazelcast/recoverDiscussion/' + contactId}`);
  }

  private retrieveDiscussion(customerId: string): Observable<IChatDiscussion> {
    return this.http.get<IChatDiscussion>(`${this.chatBaseUrl + '/discussion/' + customerId}`);
  }

  private fetchMessagesForCustomerId(customerId: string): Observable<ChatMessage[]> {
    return this.http
      .get<ChatMessage[]>(`${this.chatBaseUrl}/messages/${customerId}`)
      .pipe(map(messages => messages.map(message => ChatMessage.fromWebSocket(message))));
  }
}
